import { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebase";
import StorageImage from "../components/StorageImage";
import { removeCachedStorageUrl } from "../utils/storageCache";

const STORE_COLLECTION_PATH = ["config", "shop", "items"];

const ITEM_TYPES = ["lifeguard", "timefreeze", "token"];

const DEFAULT_FORM = {
  itemId: "",
  title: "",
  desc: "",
  type: "lifeguard",
  quantity: 1,
  iconFile: null,
  iconKey: "",
  sort: 999,
  enabled: true,
};

const TYPE_LABELS = {
  lifeguard: "Salvavidas",
  timefreeze: "Time Freeze",
  token: "Token",
};

const TYPE_EMOJIS = {
  lifeguard: "🛡️",
  timefreeze: "❄️",
  token: "🎟️",
};

function normalizeItemId(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
}

function getCollectionRef() {
  return collection(db, ...STORE_COLLECTION_PATH);
}

function getDocRef(itemId) {
  return doc(db, ...STORE_COLLECTION_PATH, itemId);
}

function buildIconPath(itemId) {
  return itemId ? `UI/Shop/${itemId}` : "";
}

function getTimestampVersion(value) {
  if (!value) return "";

  if (typeof value === "number" || typeof value === "string") {
    return String(value);
  }

  if (typeof value?.seconds === "number") {
    return String(value.seconds);
  }

  if (typeof value?.toMillis === "function") {
    return String(value.toMillis());
  }

  return "";
}

function LocalFilePreview({ file, className, fallback }) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  if (!previewUrl) {
    return <span className={className}>{fallback}</span>;
  }

  return <img className={className} src={previewUrl} alt="Preview" />;
}

function StoreSwitch({ checked, onChange, disabled }) {
  return (
    <label className="switch-row">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="switch-ui"></span>
      <strong>{checked ? "Activo" : "Inactivo"}</strong>
    </label>
  );
}

function StorePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ ...DEFAULT_FORM });

  const enabledItems = useMemo(
    () => items.filter((item) => item.enabled).length,
    [items]
  );

  const disabledItems = useMemo(
    () => items.filter((item) => !item.enabled).length,
    [items]
  );

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return items.filter((item) => {
      const id = String(item.id || "").toLowerCase();
      const title = String(item.title || "").toLowerCase();
      const desc = String(item.desc || "").toLowerCase();
      const type = String(item.type || "").toLowerCase();
      const iconKey = String(item.iconKey || "").toLowerCase();

      return (
        id.includes(normalizedSearch) ||
        title.includes(normalizedSearch) ||
        desc.includes(normalizedSearch) ||
        type.includes(normalizedSearch) ||
        iconKey.includes(normalizedSearch)
      );
    });
  }, [items, searchText]);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);

      const itemsQuery = query(getCollectionRef(), orderBy("sort", "asc"));
      const snapshot = await getDocs(itemsQuery);

      const data = snapshot.docs.map((docItem) => ({
        id: docItem.id,
        ...docItem.data(),
      }));

      setItems(data);
    } catch (error) {
      console.error("Error cargando objetos de tienda:", error);
      alert("No se pudieron cargar los objetos de tienda.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  function updateForm(field, value) {
    setForm((prev) => {
      const next = {
        ...prev,
        [field]: value,
      };

      if (field === "itemId") {
        const normalizedId = normalizeItemId(value);
        next.itemId = normalizedId;

        const previousAutoPath = buildIconPath(prev.itemId);

        if (!prev.iconKey || prev.iconKey === previousAutoPath) {
          next.iconKey = buildIconPath(normalizedId);
        }
      }

      return next;
    });
  }

  function resetFileInput() {
    const fileInput = document.getElementById("shopIconFileInput");

    if (fileInput) {
      fileInput.value = "";
    }
  }

  function openCreateModal() {
    setEditingItem(null);
    setForm({
      ...DEFAULT_FORM,
      iconKey: "",
    });
    resetFileInput();
    setIsModalOpen(true);
  }

  function openEditModal(item) {
    setEditingItem(item);

    setForm({
      itemId: item.id,
      title: item.title || "",
      desc: item.desc || "",
      type: item.type || "lifeguard",
      quantity: item.quantity ?? 1,
      iconFile: null,
      iconKey: item.iconKey || buildIconPath(item.id),
      sort: item.sort ?? 999,
      enabled: item.enabled ?? true,
    });

    resetFileInput();
    setIsModalOpen(true);
  }

  function closeModal() {
    if (saving) return;

    setIsModalOpen(false);
    setEditingItem(null);
    setForm({ ...DEFAULT_FORM });
    resetFileInput();
  }

  function normalizeStoreItemData(data, forcedItemId = null) {
    const itemId = forcedItemId || normalizeItemId(data.itemId);
    const iconKey = String(data.iconKey || "").trim() || buildIconPath(itemId);

    return {
      itemId,
      title: String(data.title || "").trim(),
      desc: String(data.desc || "").trim(),
      type: data.type,
      iconKey,
      quantity: Math.max(1, Number(data.quantity) || 1),
      sort: Math.max(0, Number(data.sort) || 999),
      enabled: Boolean(data.enabled),
    };
  }

  function validateStoreItem(data, isEditing) {
    if (!data.itemId) {
      alert("Ingresa un ID válido para el objeto.");
      return false;
    }

    if (!data.title) {
      alert("Ingresa el título del objeto.");
      return false;
    }

    if (!data.desc) {
      alert("Ingresa la descripción del objeto.");
      return false;
    }

    if (!ITEM_TYPES.includes(data.type)) {
      alert("Selecciona un tipo válido.");
      return false;
    }

    if (!data.iconKey) {
      alert("Ingresa la ruta del icono.");
      return false;
    }

    if (!isEditing && !form.iconFile) {
      alert("Selecciona el icono del objeto.");
      return false;
    }

    return true;
  }

  async function uploadIconIfNeeded(itemId, iconFile, currentIconKey = "") {
    const iconPath = String(currentIconKey || "").trim() || buildIconPath(itemId);

    if (!iconFile) {
      return {
        iconKey: iconPath,
        iconVersion: null,
      };
    }

    const iconVersion = Date.now();

    await uploadBytes(ref(storage, iconPath), iconFile, {
      contentType: iconFile.type || "image/png",
      cacheControl: "public,max-age=604800",
      customMetadata: {
        version: String(iconVersion),
      },
    });

    removeCachedStorageUrl(iconPath);

    return {
      iconKey: iconPath,
      iconVersion,
    };
  }

  async function handleSaveItem(event) {
    event.preventDefault();

    const isEditing = Boolean(editingItem);
    const itemId = isEditing ? editingItem.id : normalizeItemId(form.itemId);
    const itemData = normalizeStoreItemData(form, itemId);

    if (!validateStoreItem(itemData, isEditing)) return;

    try {
      setSaving(true);

      const uploadedIcon = await uploadIconIfNeeded(
        itemId,
        form.iconFile,
        itemData.iconKey
      );

      const firestoreData = {
        title: itemData.title,
        desc: itemData.desc,
        type: itemData.type,
        iconKey: uploadedIcon.iconKey,
        quantity: itemData.quantity,
        sort: itemData.sort,
        enabled: itemData.enabled,
        updatedAt: serverTimestamp(),
      };

      if (uploadedIcon.iconVersion) {
        firestoreData.iconVersion = uploadedIcon.iconVersion;
      }

      if (isEditing) {
        await updateDoc(getDocRef(itemId), firestoreData);

        setItems((prevItems) =>
          prevItems
            .map((item) =>
              item.id === itemId
                ? {
                    ...item,
                    ...firestoreData,
                    id: itemId,
                    updatedAt: new Date(),
                  }
                : item
            )
            .sort((a, b) => Number(a.sort ?? 999) - Number(b.sort ?? 999))
        );
      } else {
        await setDoc(getDocRef(itemId), firestoreData);

        setItems((prevItems) =>
          [
            {
              id: itemId,
              ...firestoreData,
              updatedAt: new Date(),
            },
            ...prevItems,
          ].sort((a, b) => Number(a.sort ?? 999) - Number(b.sort ?? 999))
        );
      }

      closeModal();

      alert(
        isEditing
          ? "Objeto actualizado correctamente."
          : "Objeto creado correctamente."
      );
    } catch (error) {
      console.error("Error guardando objeto:", error);
      alert("No se pudo guardar el objeto.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="admin-section-card store-page">
        <div className="section-header store-header">
          <div>
            <span className="section-eyebrow">Economía</span>
            <h2>Tienda</h2>
            <p>Administra los objetos disponibles en la tienda del juego.</p>
          </div>

          <div className="section-actions">
            <button
              className="primary-action-button"
              type="button"
              onClick={openCreateModal}
              disabled={saving}
            >
              ＋ Añadir objeto
            </button>

            <button
              className="secondary-button"
              type="button"
              onClick={loadItems}
              disabled={saving}
            >
              🔄 Actualizar
            </button>

            <span className="section-count">
              {filteredItems.length} / {items.length} objetos
            </span>
          </div>
        </div>

        <div className="store-summary-grid">
          <article className="store-summary-card">
            <span>🛒</span>
            <div>
              <strong>{items.length}</strong>
              <small>Total objetos</small>
            </div>
          </article>

          <article className="store-summary-card">
            <span>✅</span>
            <div>
              <strong>{enabledItems}</strong>
              <small>Activos</small>
            </div>
          </article>

          <article className="store-summary-card">
            <span>⏸️</span>
            <div>
              <strong>{disabledItems}</strong>
              <small>Inactivos</small>
            </div>
          </article>
        </div>

        <div className="users-toolbar">
          <div className="search-box users-search-box">
            <span>🔎</span>

            <input
              type="text"
              placeholder="Buscar por título, tipo, descripción o icono..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {searchText && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => setSearchText("")}
            >
              Limpiar
            </button>
          )}
        </div>

        {loading ? (
          <main className="users-loading-state">
            <section className="loading-card">
              <div className="banter-loader" aria-hidden="true">
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
                <div className="banter-loader__box"></div>
              </div>

              <h2>Cargando tienda...</h2>
              <p>Consultando objetos configurados.</p>
            </section>
          </main>
        ) : (
          <div className="table-wrapper">
            <table className="admin-table store-table">
              <thead>
                <tr>
                  <th>Objeto</th>
                  <th>Tipo</th>
                  <th>Cantidad</th>
                  <th>Orden</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="store-item-cell">
                        <span className="store-item-avatar store-item-avatar--image">
                          <StorageImage
                            path={item.iconKey}
                            version={
                              item.iconVersion ||
                              getTimestampVersion(item.updatedAt) ||
                              ""
                            }
                            alt={item.title || item.id}
                            className="store-item-icon-image"
                            fallback={TYPE_EMOJIS[item.type] || "📦"}
                          />
                        </span>

                        <div>
                          <strong>{item.title || "Sin título"}</strong>
                          <span>{item.id}</span>
                          <small>{item.desc || "Sin descripción"}</small>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className="type-badge">
                        <span>{TYPE_EMOJIS[item.type] || "📦"}</span>
                        {TYPE_LABELS[item.type] || item.type || "—"}
                      </span>
                    </td>

                    <td>
                      <strong className="quantity-pill">
                        ×{item.quantity ?? 1}
                      </strong>
                    </td>

                    <td>
                      <code className="table-code">{item.sort ?? 999}</code>
                    </td>

                    <td>
                      <span
                        className={
                          item.enabled
                            ? "status-badge status-badge--enabled"
                            : "status-badge status-badge--disabled"
                        }
                      >
                        {item.enabled ? "Activo" : "Inactivo"}
                      </span>
                    </td>

                    <td>
                      <button
                        className="small-button small-button--dark"
                        type="button"
                        onClick={() => openEditModal(item)}
                        disabled={saving}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredItems.length === 0 && (
              <p className="empty-state">No hay objetos para mostrar.</p>
            )}
          </div>
        )}
      </section>

      {isModalOpen && (
        <div className="modal-backdrop">
          <div className="edit-modal store-edit-modal">
            <div className="edit-modal__header">
              <div>
                <span className="section-eyebrow">
                  {editingItem ? "Editar" : "Nuevo objeto"}
                </span>

                <h3>{editingItem ? "Editar objeto" : "Añadir objeto"}</h3>

                <p>
                  {editingItem
                    ? `Editando ${editingItem.id}`
                    : "Crea un nuevo objeto para la tienda."}
                </p>
              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeModal}
                disabled={saving}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveItem} className="edit-form">
              <label>
                ID del objeto
                <input
                  type="text"
                  value={form.itemId}
                  onChange={(e) => updateForm("itemId", e.target.value)}
                  placeholder="1_lifeguard"
                  disabled={saving || Boolean(editingItem)}
                />
                <small className="field-hint">
                  Será el ID del documento. Al escribirlo se sugiere una ruta,
                  pero puedes cambiarla abajo.
                </small>
              </label>

              <label>
                Ruta del icono
                <input
                  type="text"
                  value={form.iconKey}
                  onChange={(e) => updateForm("iconKey", e.target.value)}
                  placeholder="UI/Shop/1_lifeguard"
                  disabled={saving}
                />
                <small className="field-hint">
                  Se usará esta ruta exacta para mostrar y reemplazar el icono.
                </small>
              </label>

              <label>
                Título
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => updateForm("title", e.target.value)}
                  placeholder="1 salvavidas"
                  disabled={saving}
                />
              </label>

              <label>
                Descripción
                <textarea
                  value={form.desc}
                  onChange={(e) => updateForm("desc", e.target.value)}
                  placeholder="Objeto para continuar la partida luego de perder"
                  disabled={saving}
                  rows={3}
                />
              </label>

              <div className="edit-form-grid">
                <label>
                  Tipo
                  <select
                    value={form.type}
                    onChange={(e) => updateForm("type", e.target.value)}
                    disabled={saving}
                  >
                    <option value="lifeguard">Salvavidas</option>
                    <option value="timefreeze">Time Freeze</option>
                    <option value="token">Token</option>
                  </select>
                </label>

                <label>
                  Cantidad
                  <input
                    type="number"
                    min="1"
                    value={form.quantity}
                    onChange={(e) => updateForm("quantity", e.target.value)}
                    disabled={saving}
                  />
                </label>

                <label>
                  Icono
                  <input
                    id="shopIconFileInput"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) =>
                      updateForm("iconFile", e.target.files?.[0] || null)
                    }
                    disabled={saving}
                  />
                  <small className="field-hint">
                    {editingItem
                      ? "Opcional. Si subes uno nuevo, reemplazará la ruta configurada."
                      : "Obligatorio. Se subirá a la ruta configurada."}
                  </small>
                </label>

                <label>
                  Orden
                  <input
                    type="number"
                    min="0"
                    value={form.sort}
                    onChange={(e) => updateForm("sort", e.target.value)}
                    disabled={saving}
                  />
                </label>
              </div>

              <div className="store-preview-card">
                <div className="store-preview-avatar store-preview-avatar--image">
                  {form.iconFile ? (
                    <LocalFilePreview
                      file={form.iconFile}
                      className="store-preview-image"
                      fallback={TYPE_EMOJIS[form.type] || "📦"}
                    />
                  ) : form.iconKey ? (
                    <StorageImage
                      path={form.iconKey}
                      version={
                        editingItem?.iconVersion ||
                        getTimestampVersion(editingItem?.updatedAt) ||
                        ""
                      }
                      alt={form.title || "Icono"}
                      className="store-preview-image"
                      fallback={TYPE_EMOJIS[form.type] || "📦"}
                    />
                  ) : (
                    TYPE_EMOJIS[form.type] || "📦"
                  )}
                </div>

                <div>
                  <span>Vista previa</span>
                  <strong>{form.title || "Objeto de tienda"}</strong>
                  <small>{form.desc || "Descripción del objeto"}</small>
                </div>

                <strong className="quantity-pill">×{form.quantity || 1}</strong>
              </div>

              <div className="edit-modal__actions">
                <StoreSwitch
                  checked={form.enabled}
                  onChange={(value) => updateForm("enabled", value)}
                  disabled={saving}
                />

                <div className="edit-modal__actions-right">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Cancelar
                  </button>

                  <button
                    type="submit"
                    className="primary-action-button"
                    disabled={saving}
                  >
                    {saving
                      ? "Guardando..."
                      : editingItem
                        ? "Guardar cambios"
                        : "Crear objeto"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default StorePage;